import {
  Checkbox,
  UpdateIconButton,
  formatCurrency,
  formatDate,
  formatMinutes,
  ListMenu,
  LockedIcon,
  pn,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@compass/core";

import { ListBeProperty } from "components";
import {
  ContributorMatchListDto,
  ContributorMatchListResource,
  Permission,
  PermissionAction,
  SortObject,
} from "generated/openApi";

import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import { contributorMatchPageRoute } from "../ContributorMatchPage/ContributorMatchPage";
import { useContributorMatchMenuItems } from "./useContributorMatchMenuItems";

export const ContributorMatchTable = (props: {
  rootPermission: Permission;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  contributorMatches: ContributorMatchListDto[];
  listTotal: number | undefined;
  resource: ContributorMatchListResource;
  searchFocused: boolean;
  sortObjects: SortObject[] | undefined;
  sort: (sortObjects: SortObject[]) => void;
  selectId: (id: number) => void;
  selectedIds: number[];
  setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
  getIdsPromise: () => Promise<any>;
  loadList: () => void;
}) => {
  const navigate = useNavigate();
  const properties = props.resource.properties || [];

  const menuItems = useContributorMatchMenuItems({
    permission: props.rootPermission,
    setSelectedIds: props.setSelectedIds,
    loadList: props.loadList,
  });

  return (
    <TableContainer ref={props.tableContainerRef}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {menuItems.length > 0 && (
              <TableCell type="select">
                <ListMenu
                  selectedIds={props.selectedIds}
                  setSelectedIds={props.setSelectedIds}
                  listTotal={props.listTotal}
                  fetchFunction={() => props.getIdsPromise()}
                  actionMenuItems={menuItems}
                />
              </TableCell>
            )}

            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ContributorMatchListDto>("date"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ContributorMatchListDto>("service"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ContributorMatchListDto>("contributor"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find(
                  (x) => x.propertyName === pn<ContributorMatchListDto>("durationInMinutes")
                )}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell align="right">
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ContributorMatchListDto>("amount"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell align="right">
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ContributorMatchListDto>("benefitAmount"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell align="right">
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ContributorMatchListDto>("medicalAmount"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell align="right">
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ContributorMatchListDto>("files"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.contributorMatches.map((contributorMatchItem) => (
            <TableRow
              key={contributorMatchItem.id}
              onClick={() =>
                navigate({
                  pathname: contributorMatchPageRoute.getPath({
                    id: contributorMatchItem.id,
                    crudMode: "view",
                    screenType: "standard",
                  }),
                })
              }
            >
              {menuItems.length > 0 && (
                <TableCell type="select">
                  <Checkbox
                    checked={props.selectedIds.includes(contributorMatchItem.id)}
                    onClick={(e) => props.selectId(contributorMatchItem.id)}
                  />
                </TableCell>
              )}
              <TableCell>{formatDate(contributorMatchItem.date)}</TableCell>
              <TableCell>{contributorMatchItem.service}</TableCell>
              <TableCell>{contributorMatchItem.contributor}</TableCell>
              <TableCell>{formatMinutes(contributorMatchItem.durationInMinutes)}</TableCell>
              <TableCell align="right">{formatCurrency(contributorMatchItem.amount)}</TableCell>
              <TableCell align="right">{formatCurrency(contributorMatchItem.benefitAmount)}</TableCell>
              <TableCell align="right">{formatCurrency(contributorMatchItem.medicalAmount)}</TableCell>
              <TableCell align="right">{contributorMatchItem.files > 0 && contributorMatchItem.files}</TableCell>
              <TableCell type="action">
                {contributorMatchItem.isLocked === true && <LockedIcon />}

                {isAuthorized(props.rootPermission, PermissionAction.Update) &&
                  !contributorMatchItem.isBeforeMatchStaticEndDate &&
                  (!contributorMatchItem.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock)) && (
                    <UpdateIconButton
                      onClick={() =>
                        navigate({
                          pathname: contributorMatchPageRoute.getPath({
                            id: contributorMatchItem.id,
                            crudMode: "edit",
                            screenType: "standard",
                          }),
                        })
                      }
                    />
                  )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
